import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { Personen, Email, PersonCrmCompareData } from '../../../_shared/interfaces';
import { PersonsStoreService, IAlertType, AlertService, CrmStoreService } from '../../../_shared/services';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgFor, NgIf } from "@angular/common";
import { ClrIconModule, ClrModalModule } from "@clr/angular";
import { ProjectCardComponent } from "../../projects/project-card/project-card.component";
import { ContactCardComponent } from "../../contacts/contact-card/contact-card.component";
import { ClarityIcons, twoWayArrowsIcon } from "@cds/core/icon";
import { getTenantStorage } from '../../../_shared/utils/tenant-context';
import { take } from "rxjs/operators";

import { environment as env } from "../../../../environments/environment";

@Component({
  selector: 'app-person-details',
  standalone: true,
  imports: [
    RouterLink,
    NgIf,
    NgFor,
    ClrIconModule,
    ProjectCardComponent,
    ClrModalModule,
    ContactCardComponent
  ],
  templateUrl: './person-details.component.html',
  styleUrl: './person-details.component.sass'
})
export class PersonDetailsComponent implements OnInit, OnChanges {
  person: Personen | null = null;
  personId: number = 0;
  public makeACall = false;
  public callTarget: string = '';
  devMode: boolean;
  crmMode: boolean;
  crmCompare: PersonCrmCompareData | null = null;
  crmCompareLoading = false;
  crmSyncRunning = false;

  @Input() newEmail: Email | null = null;
  @Output() submitEmail = new EventEmitter<Email>();

  constructor(
    private perss: PersonsStoreService,
    private crmss: CrmStoreService,
    private router: Router,
    private route: ActivatedRoute,
    private alertDispatcher: AlertService
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon);
    this.devMode = env.stages.dev;
    this.crmMode = env.externalCrm;
  }

  ngOnInit() {
    const params = this.route.snapshot.paramMap;
    this.personId = parseInt(params.get('pid') ?? '0', 10);
    this.loadSingleContact(this.personId);
  }

  ngOnChanges() {}

  loadSingleContact(contactID: number) {
    let activpassiv: boolean;
    this.perss.getSinglePersons(contactID)
      .subscribe({
        next: res => {
          if (!res || !res.object || res.object.length === 0) {
            this.alertDispatcher.show({
              message: ' Keine Personen gefunden! ',
              clrAlertType: IAlertType.WARNING
            }, 2000);
          }
          this.person = res.object[0];
          activpassiv = this.person.active;
          if (this.crmMode) {
            this.loadCrmCompare(this.person.pid);
          }

        },
        complete: () => {
          switch (activpassiv) {
            case false:
              this.alertDispatcher.alertConfig.message = 'Bitte beachten: Diese Person wurde auf "passiv" gesetzt!';
              this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
              break;
            default:
              this.alertDispatcher.alertConfig.message = 'All engines running! :) ';
              this.alertDispatcher.alertConfig.clrAlertType = IAlertType.SUCCESS;
              break;
          }
        }
      });
  }

  deletePerson(cid: number, pid: number, firstname: string, lastname: string) {
    if (confirm(firstname + ' ' + lastname + ' (' + pid + ') wirklich löschen?')) {
      this.perss.deletePerson(pid)
        .subscribe(res => this.router.navigate(['/contact/details', cid ], { relativeTo: this.route }));
    }
  }

  callUrl(phoneNumber: string) {
    const normalizedPhoneNumber = phoneNumber.replace(/[^\d+]/g, '');
    return `tel:${normalizedPhoneNumber}`;
  }

  startCall(phoneNumber: string) {
    window.location.href = this.callUrl(phoneNumber);
  }

  get crmId(): string {
    const value = this.person?.crm_id;
    return value === null || value === undefined ? '' : value.toString();
  }

  get crmIdStatus(): string {
    if (!this.crmMode) return '';
    return this.crmId ? 'OK' : 'Fehlt';
  }

  loadCrmCompare(personId: number) {
    const sessionId = this.crmss.getCrmToken() ?? '';
    this.crmCompareLoading = true;
    this.perss.getCrmCompare(personId, sessionId).subscribe({
      next: (res) => {
        this.crmCompare = res?.data ?? null;
      },
      error: () => {
        this.crmCompare = null;
      },
      complete: () => {
        this.crmCompareLoading = false;
      }
    });
  }

  get crmCompareLabel(): string {
    if (!this.crmMode) return 'CRM aus';
    if (this.crmCompareLoading) return 'Pruefung laeuft';
    if (!this.crmCompare) return 'Nicht verfuegbar';
    return this.crmCompare.in_sync ? 'Synchron' : 'Abweichung';
  }

  get crmCompareClass(): string {
    if (!this.crmMode) return 'label';
    if (this.crmCompareLoading) return 'label label-info';
    if (!this.crmCompare) return 'label label-warning';
    return this.crmCompare.in_sync ? 'label label-success' : 'label label-danger';
  }

  getDiffFieldLabel(field: string): string {
    const labels: Record<string, string> = {
      company: 'Firma',
      firstname: 'Vorname',
      lastname: 'Nachname',
      email: 'E-Mail',
      phone: 'Telefon',
      mobile: 'Mobil',
      position: 'Position'
    };
    return labels[field] ?? field;
  }

  syncCrmNow() {
    if (!this.person || this.crmSyncRunning) return;
    const staffId = parseInt(getTenantStorage('staff_id') ?? getTenantStorage('user_id') ?? '0', 10);

    if (!staffId) {
      this.alertDispatcher.show({
        message: 'CRM-Sync nicht möglich: Staff-ID fehlt.',
        clrAlertType: IAlertType.WARNING
      }, 4000);
      return;
    }

    this.crmss.crmProviderName().pipe(take(1)).subscribe(provider => {
      const sessionId = this.crmss.getCrmToken() ?? '';

      if (provider === 'tecart' && !sessionId) {
        this.alertDispatcher.show({
          message: 'CRM-Sync nicht möglich: CRM-Token fehlt.',
          clrAlertType: IAlertType.WARNING
        }, 4000);
        return;
      }

      this.crmSyncRunning = true;
      this.perss.syncCrmPerson(this.person!.pid, {
        session_id: sessionId,
        staff_id: staffId
      }).subscribe({
        next: (res) => {
          this.crmCompare = res?.data ?? this.crmCompare;
          this.alertDispatcher.show({
            message: this.crmCompare?.in_sync ? 'CRM-Sync erfolgreich.' : 'CRM-Sync mit Abweichungen abgeschlossen.',
            clrAlertType: this.crmCompare?.in_sync ? IAlertType.SUCCESS : IAlertType.WARNING
          }, 3000);
        },
        error: () => {
          this.alertDispatcher.show({
            message: 'CRM-Sync fehlgeschlagen.',
            clrAlertType: IAlertType.DANGER
          }, 4000);
        },
        complete: () => {
          this.crmSyncRunning = false;
        }
      });
    });
  }
}
